import { FeedbackPopover } from '@/components/chat/FeedbackPopover';
import {
  mkTextFeedbackBtnTrackAttrs,
  mkThumbsDownBtnTrackAttrs,
  mkThumbsUpBtnTrackAttrs,
} from '@/analytics/props/feedback';
import { Reaction } from '@/models/Reaction';

import { IconButton } from '@mui/material';
import { MouseEventHandler, ReactNode, useState } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import styled from 'styled-components';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';

type FeedbackButtonsProps = {
  viewerReaction: Reaction | null | undefined;
  onUpdateReactionState: (newReaction: Reaction | null) => Promise<void>;
  onSubmitFeedback: (feedback: string) => Promise<void>;
  theme?: FeedbackButtonsTheme;
  heapPrefix?: string;
  heapProps?: Record<string, any>;
  // For additional buttons
  children?: ReactNode;
};

export enum FeedbackButtonsTheme {
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

export const defaultReactionUpText =
  'Any additional comments on why it is helpful? (optional)';
export const defaultReactionDownText =
  'Any additional comments on what went wrong? (optional)';

// tracks which button opened the popover
enum FeedbackOpenerSource {
  ReactionUp = Reaction.THUMBS_UP,
  ReactionDown = Reaction.THUMBS_DOWN,
  Feedback = 'Feedback',
}
type FeedbackOpenState = FeedbackOpenerSource | false;

export const FeedbackButtons = ({
  onUpdateReactionState,
  onSubmitFeedback,
  viewerReaction,
  theme,
  heapPrefix,
  heapProps,
  children,
}: FeedbackButtonsProps) => {
  const [feedbackOpener, setFeedbackOpener] =
    useState<FeedbackOpenState>(false);
  const [feedbackFormAnchorEl, setFeedbackFormAnchorEl] =
    useState<Element | null>(null);

  const [feedbackPlaceholder, setFeedbackPlaceholder] = useState('');

  const setFeedbackOpen = (
    opener: FeedbackOpenState,
    anchor: Element | null = null,
    defaultMessageText: string = 'Feedback',
  ) => {
    if (opener) {
      setFeedbackPlaceholder(defaultMessageText);
      if (anchor) {
        setFeedbackFormAnchorEl(anchor);
      }
    }
    setFeedbackOpener(opener);
  };

  const onClickThumbsUp: MouseEventHandler = (event): void => {
    if (viewerReaction !== Reaction.THUMBS_UP) {
      event.stopPropagation();
      onUpdateReactionState(Reaction.THUMBS_UP);
      setFeedbackOpen(
        FeedbackOpenerSource.ReactionUp,
        event.currentTarget,
        defaultReactionUpText,
      );
    } else {
      onUpdateReactionState(null);
    }
  };

  const onClickThumbsDown: MouseEventHandler = (event): void => {
    if (viewerReaction !== Reaction.THUMBS_DOWN) {
      event.stopPropagation();
      onUpdateReactionState(Reaction.THUMBS_DOWN);
      setFeedbackOpen(
        FeedbackOpenerSource.ReactionDown,
        event.currentTarget,
        defaultReactionDownText,
      );
    } else {
      onUpdateReactionState(null);
    }
  };

  const onClickCustomFeedbackButton: MouseEventHandler = (event) => {
    event.stopPropagation();
    setFeedbackOpen(
      feedbackOpener !== FeedbackOpenerSource.Feedback
        ? FeedbackOpenerSource.Feedback
        : false,
      event.currentTarget,
    );
  };

  const onCloseFeedbackForm = () => {
    setFeedbackOpen(false);
  };

  return (
    <ButtonRow>
      <ReactionButton
        onClick={onClickThumbsUp}
        mode={theme}
        size="small"
        data-testid="feedback-reaction-thumbs-up"
        {...(heapPrefix ? mkThumbsUpBtnTrackAttrs(heapPrefix, heapProps) : {})}
      >
        {viewerReaction === Reaction.THUMBS_UP ? (
          <ThumbUpIcon fontSize="small" />
        ) : (
          <ThumbUpOffAltIcon fontSize="small" />
        )}
      </ReactionButton>
      <ReactionButton
        onClick={onClickThumbsDown}
        mode={theme}
        size="small"
        data-testid="feedback-reaction-thumbs-down"
        {...(heapPrefix
          ? mkThumbsDownBtnTrackAttrs(heapPrefix, heapProps)
          : {})}
      >
        {viewerReaction === Reaction.THUMBS_DOWN ? (
          <ThumbDownIcon fontSize="small" />
        ) : (
          <ThumbDownOffAltIcon fontSize="small" />
        )}
      </ReactionButton>
      <ReactionButton
        onClick={onClickCustomFeedbackButton}
        mode={theme}
        size="small"
        data-testid="feedback-open-button"
        {...(heapPrefix
          ? mkTextFeedbackBtnTrackAttrs(heapPrefix, heapProps)
          : {})}
      >
        <ChatBubbleOutlineIcon fontSize="small" />
      </ReactionButton>

      {children}
      <FeedbackPopover
        isFeedbackFormOpen={!!feedbackOpener}
        closeFeedbackForm={onCloseFeedbackForm}
        feedbackFormAnchorEl={feedbackFormAnchorEl}
        onSubmitFeedback={onSubmitFeedback}
        messagePlaceholder={feedbackPlaceholder}
        heapPrefix={heapPrefix}
        heapProps={heapProps}
      />
    </ButtonRow>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const ReactionButton = styled(IconButton)<{
  mode?: FeedbackButtonsTheme;
}>`
  &.MuiIconButton-root {
    border-radius: 4px;
    background: ${(props) =>
      props.mode === FeedbackButtonsTheme.LIGHT
        ? props.theme.color['cream-100'].hex
        : 'rgba(250, 242, 233, 0.04)'};

    &:hover {
      background: ${(props) =>
        props.mode === FeedbackButtonsTheme.LIGHT
          ? props.theme.color['cream-60'].hex
          : 'rgba(250, 242, 233, 0.1)'};
    }
  }

  & .MuiSvgIcon-root {
    color: ${(props) =>
      props.mode === FeedbackButtonsTheme.LIGHT
        ? props.theme.color['pink-100'].hex
        : props.theme.color['green-100'].hex};
  }
`;
